import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowLeft, ShoppingCart, Lock, X } from "lucide-react";

import { useCart } from "@/components/CartProvider";
import { formatPrice } from "@/lib/formatPrice";
import { apiFetch } from "@/lib/api";
import CheckoutForm from "@/components/CheckoutForm";
import ShippingMethodSelector from "@/components/ShippingMethodSelector";
import { useShopSettings, DEFAULT_SHOP_SETTINGS } from "@/hooks/useShopSettings";

interface ShippingMethod {
  id: number;
  name: string;
  price: number;
  is_pickup: boolean;
}

interface OrderResponse {
  id: number;
}

interface UpdatedItemPrice {
  productId: number;
  name: string;
  price: number;
  salePrice: number | null;
}

interface OrderError extends Error {
  items?: UpdatedItemPrice[];
  productId?: number;
}

const DEFAULT_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  zip: "",
};

export default function Cart() {
  const { items, removeItem, clearCart, total, updateItemPrices } = useCart();
  const navigate = useNavigate();
  const { data: settings } = useShopSettings();

  const [selectedShippingId, setSelectedShippingId] = useState<number | null>(null);
  const [saveInfo, setSaveInfo] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [form, setForm] = useState(() => {
    try {
      const saved = document.cookie
        .split("; ")
        .find((c) => c.startsWith("sosline_customer="));
      if (saved) {
        const parsed = JSON.parse(decodeURIComponent(saved.split("=")[1]!));
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return { ...DEFAULT_FORM, ...parsed };
        }
      }
    } catch {}
    return DEFAULT_FORM;
  });

  const { data: shippingMethods = [], isError: shippingMethodsError } = useQuery<ShippingMethod[]>({
    queryKey: ["shipping-methods"],
    queryFn: () => apiFetch<ShippingMethod[]>("/api/shipping-methods"),
  });

  const selectedShipping = shippingMethods.find((m) => m.id === selectedShippingId) ?? null;
  const shippingCost = selectedShipping?.price ?? 0;
  const grandTotal = total + shippingCost;
  const isPickup = selectedShipping?.is_pickup ?? false;

  // Drop the selection if the chosen method is no longer in the active list
  useEffect(() => {
    if (selectedShippingId !== null && selectedShipping === null && !shippingMethodsError) {
      setSelectedShippingId(null);
    }
  }, [selectedShippingId, selectedShipping, shippingMethodsError]);

  const mutation = useMutation({
    mutationFn: async (payload: object): Promise<OrderResponse> => {
      const res = await fetch("/api/orders", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const err = new Error((body as any).error || res.statusText) as OrderError;
        if (Array.isArray((body as any).items)) err.items = (body as any).items;
        if (typeof (body as any).productId === "number") err.productId = (body as any).productId;
        throw err;
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (saveInfo) {
        document.cookie = `sosline_customer=${encodeURIComponent(JSON.stringify(form))}; path=/; max-age=${60 * 60 * 24 * 90}; SameSite=Strict`;
      } else {
        document.cookie = "sosline_customer=; path=/; max-age=0";
      }
      clearCart();
      navigate({ to: "/order-confirmation/$orderId", params: { orderId: String(data.id) } });
    },
    onError: (err) => {
      const e = err as OrderError;
      if (e.productId !== undefined) removeItem(e.productId);
      if (e.items?.length) {
        updateItemPrices(e.items.map((i) => ({ id: i.productId, price: i.price, salePrice: i.salePrice })));
      }
    },
  });

  function handleFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
    setForm((prev: any) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mutation.isPending) return;
    if (!validateEmail(form.email)) {
      setEmailError("Unesite ispravnu email adresu");
      return;
    }
    setEmailError("");
    if (!selectedShipping) return;

    mutation.mutate({
      shippingMethodId: selectedShipping.id,
      customer: { ...form },
      items: items.map((item) => ({
        productId: item.id,
        price: item.salePrice ?? item.price,
      })),
    });
  }

  if (items.length === 0) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 px-4">
        <div className="size-16 rounded-full bg-muted/50 flex items-center justify-center">
          <ShoppingCart className="size-7 text-muted-foreground/40" />
        </div>
        <p className="text-lg font-medium text-muted-foreground">Vaša korpa je prazna</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          Nazad na shop
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border/40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Nazad
          </Link>
          <span className="text-sm font-medium tracking-tight">
            {settings?.shop_name ?? DEFAULT_SHOP_SETTINGS.shop_name}
          </span>
          <div className="w-16" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-10">Checkout</h1>

        <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-[1fr_340px] gap-12 lg:gap-16">
            <div className="lg:col-start-2 lg:row-start-1">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-4">
                Vaša narudžba
              </h2>
              <div className="space-y-3">
                {items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 group">
                    <div className="relative">
                      <img
                        src={`/uploads/${item.id}-thumb.webp`}
                        alt={item.name}
                        className="size-14 rounded-lg object-cover bg-muted/30"
                        onError={(e) => {
                          const el = e.currentTarget;
                          el.onerror = null;
                          el.src = `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56"><rect fill="#f0efe8" width="56" height="56" rx="8"/><text x="28" y="34" text-anchor="middle" font-size="20">👕</text></svg>`)}`;
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        className="absolute -top-1.5 -right-1.5 size-5 rounded-full bg-foreground text-white flex items-center justify-center"
                        aria-label={`Ukloni ${item.name}`}
                      >
                        <X className="size-3" strokeWidth={2.5} />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-tight">{item.name}</p>
                    </div>
                    <span className="text-sm tabular-nums shrink-0">
                      {formatPrice(item.salePrice ?? item.price)} KM
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-10 lg:col-start-1 lg:row-start-1 lg:row-span-2">
              <CheckoutForm
                form={form}
                onChange={handleFieldChange}
                emailError={emailError}
                onEmailChange={(e) => {
                  handleFieldChange(e);
                  if (emailError) setEmailError("");
                }}
                saveInfo={saveInfo}
                onSaveInfoChange={setSaveInfo}
                isPickup={isPickup}
              />

              <ShippingMethodSelector
                methods={shippingMethods}
                selectedId={selectedShippingId}
                onSelect={setSelectedShippingId}
              />
              {shippingMethodsError && (
                <p className="text-xs text-red-500 mt-3">
                  Načini dostave se nisu mogli učitati. Osvježite stranicu i pokušajte ponovno.
                </p>
              )}
            </div>

            <div className="lg:col-start-2 lg:row-start-2">
              <div className="lg:sticky lg:top-8">
                <div className="border-t border-border/60 pt-4 space-y-2.5">
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Artikli ({items.length})</span>
                    <span className="tabular-nums">{formatPrice(total)} KM</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>Dostava</span>
                    <span className={`tabular-nums ${shippingCost === 0 ? "text-emerald-600" : ""}`}>
                      {selectedShipping ? (shippingCost === 0 ? "Besplatno" : `${formatPrice(shippingCost)} KM`) : "—"}
                    </span>
                  </div>
                  <div className="flex justify-between items-baseline pt-3 border-t border-border/60">
                    <span className="text-sm font-medium">Ukupno</span>
                    <span className="text-2xl font-semibold tabular-nums tracking-tight">
                      {formatPrice(grandTotal)} <span className="text-base font-normal text-muted-foreground">KM</span>
                    </span>
                  </div>
                </div>

                {mutation.error && (
                  <p className="text-sm text-red-500 text-center mt-4">
                    {(mutation.error as OrderError).message}
                  </p>
                )}

                <div className="mt-6 space-y-3">
                  {!selectedShipping && !shippingMethodsError && (
                    <p className="text-xs text-amber-600 text-center flex items-center justify-center gap-1.5">
                      <span className="size-1.5 rounded-full bg-amber-500 animate-pulse" />
                      Odaberite način dostave za nastavak
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={!selectedShipping || mutation.isPending}
                    className="group relative w-full overflow-hidden rounded-xl bg-foreground py-4 text-white font-semibold disabled:opacity-35 disabled:cursor-not-allowed transition-all duration-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.2)] hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
                  >
                    <span className="absolute inset-0 bg-gradient-to-r from-emerald-600 via-foreground to-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    <span className="relative flex items-center justify-center gap-2 text-sm">
                      {mutation.isPending ? (
                        <>
                          <span className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Šaljemo narudžbu...
                        </>
                      ) : (
                        <>
                          Potvrdi narudžbu
                          <span className="text-lg leading-none transition-transform duration-300 group-hover:translate-x-0.5">→</span>
                        </>
                      )}
                    </span>
                  </button>

                  <div className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground/50">
                    <Lock className="size-3" />
                    Plaćanje pouzećem — platite prilikom preuzimanja
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
